'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useRef, useState, } from 'react';
import styled from 'styled-components';
import { ResponsiveImage } from '../Image';
const ZoomableContainerElement = styled.div `
  overflow: hidden;
`;
const ZoomableResponsiveImageElement = styled(ResponsiveImage) `
  transition:
    transform 0.3s ease-in-out,
    scale 0.15s ease-out;

  will-change: transform, scale;
  cursor: ${o => (o.$zoomActive ? 'zoom-out' : 'zoom-in')};
  scale: ${o => (o.$zoomActive ? o.$zoomScale ?? 2.5 : 1)};
`;
const ZoomableContainerContext = createContext({
    zoomScale: 2.5,
    zoomActive: false,
    zoomPosition: { x: 0, y: 0 },
});
function deriveZoomPosition(e) {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - left) / width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - top) / height) * 100));
    return { x, y };
}
export function ZoomableContainer({ render, zoomActive: controlledZoomActive, zoomScale = 2.5, onZoomActiveChange, ...htmlProps }) {
    const [uncontrolledZoomActive, setUncontrolledZoomActive] = useState(false);
    const isControlled = controlledZoomActive !== undefined;
    const zoomActive = isControlled ? controlledZoomActive : uncontrolledZoomActive;
    const [zoomPosition, setZoomPosition] = useState({
        x: 0,
        y: 0,
    });
    const setZoomActive = useCallback((nextZoomActive) => {
        if (!isControlled)
            setUncontrolledZoomActive(nextZoomActive);
        onZoomActiveChange?.(nextZoomActive);
    }, [isControlled, onZoomActiveChange]);
    const containerRefCallback = useCallback((node) => {
        if (!node || !zoomActive)
            return;
        const handleOutsideClick = (event) => {
            if (node.contains(event.target))
                return;
            setZoomActive(false);
        };
        document.addEventListener('click', handleOutsideClick, true);
        return () => document.removeEventListener('click', handleOutsideClick, true);
    }, [zoomActive, setZoomActive]);
    const lastPointerTypeRef = useRef('mouse');
    const handlePointerDown = useCallback((e) => {
        lastPointerTypeRef.current = e.pointerType;
    }, []);
    const handlePositionChange = useCallback((e) => {
        if (!zoomActive || e.pointerType !== 'mouse')
            return;
        setZoomPosition(deriveZoomPosition(e));
    }, [setZoomPosition, zoomActive]);
    const handleClick = useCallback((e) => {
        if (lastPointerTypeRef.current !== 'mouse')
            return;
        if (!zoomActive)
            setZoomPosition(deriveZoomPosition(e));
        setZoomActive(!zoomActive);
    }, [setZoomActive, zoomActive]);
    const ctx = { zoomActive, zoomPosition, zoomScale };
    return (_jsx(ZoomableContainerElement, { ref: containerRefCallback, onClick: handleClick, onPointerDown: handlePointerDown, onPointerMove: handlePositionChange, ...htmlProps, children: _jsx(ZoomableContainerContext, { value: ctx, children: typeof render === 'function' ? render(ctx) : render }) }));
}
export function ZoomableResponsiveImage(props) {
    const { zoomActive, zoomPosition, zoomScale } = useContext(ZoomableContainerContext);
    return (_jsx(ZoomableResponsiveImageElement, { "$zoomActive": zoomActive, "$zoomScale": zoomScale, style: { transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%` }, draggable: false, ...props }));
}
//# sourceMappingURL=index.js.map